<template>
  <div class="mobile-admin-page">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="backToModule"
      />
    </div>

    <div class="page-shell">
      <div class="card-strip">
        <div class="text-operations">
          <h2>Encuesta de calidad (app móvil)</h2>
        </div>
      </div>

      <div class="action-buttons">
        <button class="action-button" @click="backToModule">Volver al menú</button>
        <button
          class="action-button action-primary"
          :disabled="saving || loading"
          @click="save"
        >
          {{ saving ? 'Guardando…' : 'Guardar cambios' }}
        </button>
      </div>

      <div class="table-container card-wrap">
        <h2>Preguntas</h2>
        <p style="margin: 0 0 12px; font-size: 13px; color: #666;">
          Estas preguntas se muestran al cliente en la pestaña Encuesta de cada servicio.
          Todas se responden con la misma escala de 5 caritas (Muy insatisfecho → Muy
          satisfecho) y la encuesta incluye siempre un campo de comentarios. Los cambios
          aplican a encuestas futuras; las ya respondidas conservan el texto original.
        </p>

        <p v-if="loadError" class="text-danger" style="margin: 8px 0;">
          {{ loadError }}
          <button class="action-button" style="padding: 4px 10px; font-size: 12px; margin-left: 8px;" @click="fetchTemplate">
            Reintentar
          </button>
        </p>

        <div v-if="loading" style="padding: 24px; text-align: center; color: #888;">
          Cargando…
        </div>

        <template v-else>
          <div
            v-for="(question, index) in questions"
            :key="question.key || 'new-' + index"
            style="display: flex; gap: 8px; align-items: center; margin-bottom: 8px;"
          >
            <span style="font-size: 12px; color: #888; min-width: 22px; text-align: right;">
              {{ index + 1 }}.
            </span>
            <input
              v-model="question.text"
              type="text"
              maxlength="300"
              placeholder="Texto de la pregunta…"
              style="flex: 1; padding: 8px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 8px;"
            />
            <button
              class="action-button"
              style="padding: 6px 10px; font-size: 12px;"
              :disabled="index === 0"
              title="Subir"
              @click="move(index, -1)"
            >
              ↑
            </button>
            <button
              class="action-button"
              style="padding: 6px 10px; font-size: 12px;"
              :disabled="index === questions.length - 1"
              title="Bajar"
              @click="move(index, 1)"
            >
              ↓
            </button>
            <button
              class="action-button"
              style="padding: 6px 10px; font-size: 12px; background: #7f1d1d;"
              title="Eliminar"
              @click="removeQuestion(index)"
            >
              ✕
            </button>
          </div>

          <div v-if="questions.length === 0" style="padding: 16px; text-align: center; color: #888;">
            No hay preguntas. Agrega al menos una para que la encuesta aparezca en la app.
          </div>

          <button
            class="action-button"
            style="margin-top: 8px; padding: 8px 14px; font-size: 13px;"
            :disabled="questions.length >= 20"
            @click="addQuestion"
          >
            + Agregar pregunta
          </button>

          <p v-if="lastUpdated" style="margin: 14px 0 0; font-size: 12px; color: #888;">
            Última modificación: {{ lastUpdated }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'MobileSurveyAdmin',
  data() {
    return {
      permissions: [],
      isSuperuser: false,
      questions: [],
      updatedAt: null,
      updatedBy: null,
      loading: true,
      loadError: '',
      saving: false,
    };
  },
  computed: {
    canManageMobileApp() {
      return this.hasPerm('access_control.manage_mobile_app') || this.hasPerm('manage_mobile_app');
    },
    lastUpdated() {
      if (!this.updatedAt) return '';
      const date = new Date(this.updatedAt);
      const stamp = Number.isNaN(date.getTime()) ? this.updatedAt : date.toLocaleString('es-MX');
      return this.updatedBy ? `${stamp} por ${this.updatedBy}` : stamp;
    },
  },
  created() {
    this.loadPermissions();
    this.fetchCurrentUser();
    this.fetchTemplate();
  },
  methods: {
    loadPermissions() {
      try {
        const stored = localStorage.getItem('userPermissions');
        this.permissions = stored ? JSON.parse(stored) : [];
      } catch (error) {
        this.permissions = [];
      }
    },
    hasPerm(perm) {
      if (this.isSuperuser) return true;
      if (!this.permissions || this.permissions.length === 0) return false;
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`);
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY;
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        });
        this.permissions = data.permissions || this.permissions;
        this.isSuperuser = !!data.is_superuser;
        if (!this.canManageMobileApp) {
          this.$router.push({ name: 'MainPanel' });
        }
      } catch (error) {
        this.$router.push({ name: 'MainPanel' });
      }
    },
    backToModule() {
      this.$router.push({ path: '/modulo-movil' });
    },
    async fetchTemplate() {
      const apiDirectory = process.env.API_DIRECTORY;
      this.loading = true;
      this.loadError = '';
      try {
        const { data } = await axios.get(`${apiDirectory}/mobile-survey/template/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        });
        this.questions = (data.questions || []).map((q) => ({ key: q.key, text: q.text }));
        this.updatedAt = data.updated_at;
        this.updatedBy = data.updated_by;
      } catch (error) {
        this.loadError =
          error?.response?.data?.detail || 'No se pudo cargar la configuración de la encuesta.';
      } finally {
        this.loading = false;
      }
    },
    addQuestion() {
      this.questions.push({ key: '', text: '' });
    },
    removeQuestion(index) {
      this.questions.splice(index, 1);
    },
    move(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.questions.length) return;
      const copy = [...this.questions];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      this.questions = copy;
    },
    async save() {
      const cleaned = this.questions
        .map((q) => ({ key: q.key || undefined, text: (q.text || '').trim() }))
        .filter((q) => q.text);
      if (cleaned.length !== this.questions.length) {
        Swal.fire({
          title: 'Preguntas vacías',
          text: 'Elimina o completa las preguntas sin texto antes de guardar.',
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
        return;
      }

      const apiDirectory = process.env.API_DIRECTORY;
      this.saving = true;
      try {
        const { data } = await axios.put(
          `${apiDirectory}/mobile-survey/template/`,
          { questions: cleaned },
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.questions = (data.questions || []).map((q) => ({ key: q.key, text: q.text }));
        this.updatedAt = data.updated_at;
        this.updatedBy = data.updated_by;
        Swal.fire({
          title: 'Encuesta actualizada',
          text: 'Los cambios aplican de inmediato en la app.',
          icon: 'success',
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: 'No se pudo guardar',
          text: error?.response?.data?.detail || 'Error al guardar la encuesta.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped src="@/styles/mobile-admin.css"></style>
